/**
 * Canonical Feature Tokens and Entitlement Constants
 * Single Source of Truth (SSOT) matching sawport-api feature-control module
 */

export const FEATURE_TOKENS = {
  VIDEO_BANKING: 'video_banking',
  CALL_RECORDING: 'call_recording',
  CALL_TRANSCRIPT: 'call_transcript',
  AI_INSIGHTS: 'ai_insights',
  FACIAL_VERIFICATION: 'facial_verification',
  DOCUMENT_VERIFICATION: 'document_verification',
  ESIGNATURE: 'esignature',
  BROADCAST: 'broadcast',
  CALENDAR_SCHEDULING: 'calendar_scheduling',
  CHAT_MESSAGING: 'chat_messaging',
  SCREEN_SHARING: 'screen_sharing',
  CALL_TRANSFER: 'call_transfer',
  CALLBACK_REQUEST: 'callback_request',
  INTEGRATIONS: 'integrations',
  ANALYTICS_REPORTING: 'analytics_reporting',
  FEEDBACK_SURVEY: 'feedback_survey'
} as const;

export type FeatureToken = (typeof FEATURE_TOKENS)[keyof typeof FEATURE_TOKENS];

/**
 * Baseline fallback features granted to all organizations by default
 */
export const DEFAULT_FALLBACK_FEATURES: FeatureToken[] = [
  FEATURE_TOKENS.VIDEO_BANKING,
  FEATURE_TOKENS.CHAT_MESSAGING
];

export const QUOTA_KEYS = {
  MAX_AGENTS: 'max_agents',
  MAX_CONCURRENT_CALLS: 'max_concurrent_calls',
  RECORDING_RETENTION_DAYS: 'recording_retention_days',
  BROADCAST_MONTHLY_QUOTA: 'broadcast_monthly_quota',
  AI_MINUTES_QUOTA: 'ai_minutes_quota'
} as const;

export type QuotaKey = (typeof QUOTA_KEYS)[keyof typeof QUOTA_KEYS];

export interface FeatureMetadata {
  token: FeatureToken;
  name: string;
  description: string;
  category: 'core' | 'ai' | 'compliance' | 'collaboration' | 'engagement' | 'analytics';
  isCore: boolean;
}

export const FEATURE_METADATA: Record<FeatureToken, FeatureMetadata> = {
  [FEATURE_TOKENS.VIDEO_BANKING]: {
    token: FEATURE_TOKENS.VIDEO_BANKING,
    name: 'Video Banking & Audio',
    description: 'High-definition WebRTC video calling, room management, and audio streams.',
    category: 'core',
    isCore: true
  },
  [FEATURE_TOKENS.CHAT_MESSAGING]: {
    token: FEATURE_TOKENS.CHAT_MESSAGING,
    name: 'Chat & Messaging',
    description: 'Real-time text conversations, file sharing, and chat history.',
    category: 'core',
    isCore: true
  },
  [FEATURE_TOKENS.CALL_RECORDING]: {
    token: FEATURE_TOKENS.CALL_RECORDING,
    name: 'Call Recording',
    description: 'Automated and on-demand cloud video/audio recording and playback.',
    category: 'collaboration',
    isCore: false
  },
  [FEATURE_TOKENS.CALL_TRANSCRIPT]: {
    token: FEATURE_TOKENS.CALL_TRANSCRIPT,
    name: 'Call Transcription',
    description: 'Real-time and post-call speech-to-text transcription with speaker attribution.',
    category: 'ai',
    isCore: false
  },
  [FEATURE_TOKENS.AI_INSIGHTS]: {
    token: FEATURE_TOKENS.AI_INSIGHTS,
    name: 'AI Insights & Summaries',
    description: 'Automated AI conversation summarization, sentiment analysis, and topic tagging.',
    category: 'ai',
    isCore: false
  },
  [FEATURE_TOKENS.FACIAL_VERIFICATION]: {
    token: FEATURE_TOKENS.FACIAL_VERIFICATION,
    name: 'Facial Verification',
    description: 'Biometric face matching, liveness detection, and identity verification.',
    category: 'compliance',
    isCore: false
  },
  [FEATURE_TOKENS.DOCUMENT_VERIFICATION]: {
    token: FEATURE_TOKENS.DOCUMENT_VERIFICATION,
    name: 'Document Verification (eKYC)',
    description: 'Government ID extraction, MRZ reading, and automated authenticity checks.',
    category: 'compliance',
    isCore: false
  },
  [FEATURE_TOKENS.ESIGNATURE]: {
    token: FEATURE_TOKENS.ESIGNATURE,
    name: 'E-Signature (eSaini)',
    description: 'Interactive PDF document signing, field builders, workflows, and audit trails.',
    category: 'compliance',
    isCore: false
  },
  [FEATURE_TOKENS.CALENDAR_SCHEDULING]: {
    token: FEATURE_TOKENS.CALENDAR_SCHEDULING,
    name: 'Calendar & Scheduling',
    description: 'Customer appointment booking, callback request schedules, and agent calendar view.',
    category: 'collaboration',
    isCore: false
  },
  [FEATURE_TOKENS.CALLBACK_REQUEST]: {
    token: FEATURE_TOKENS.CALLBACK_REQUEST,
    name: 'Callback Requests',
    description: 'Customer callback queuing, routing, and waiting list management.',
    category: 'engagement',
    isCore: false
  },
  [FEATURE_TOKENS.CALL_TRANSFER]: {
    token: FEATURE_TOKENS.CALL_TRANSFER,
    name: 'Call Transfer',
    description: 'Seamless warm and blind in-call transfers between agents and departments.',
    category: 'collaboration',
    isCore: false
  },
  [FEATURE_TOKENS.SCREEN_SHARING]: {
    token: FEATURE_TOKENS.SCREEN_SHARING,
    name: 'Screen Sharing',
    description: 'Real-time desktop and application window sharing during video calls.',
    category: 'collaboration',
    isCore: false
  },
  [FEATURE_TOKENS.BROADCAST]: {
    token: FEATURE_TOKENS.BROADCAST,
    name: 'Agent Broadcasts',
    description: 'Organization-wide and team-targeted broadcast messaging and alerts.',
    category: 'engagement',
    isCore: false
  },
  [FEATURE_TOKENS.INTEGRATIONS]: {
    token: FEATURE_TOKENS.INTEGRATIONS,
    name: 'Integrations & Webhooks',
    description: 'Third-party calendar synchronization (Outlook, Google) and external connectors.',
    category: 'collaboration',
    isCore: false
  },
  [FEATURE_TOKENS.ANALYTICS_REPORTING]: {
    token: FEATURE_TOKENS.ANALYTICS_REPORTING,
    name: 'Analytics & Reporting',
    description: 'Comprehensive call metrics, agent performance, logs, and exportable reports.',
    category: 'analytics',
    isCore: false
  },
  [FEATURE_TOKENS.FEEDBACK_SURVEY]: {
    token: FEATURE_TOKENS.FEEDBACK_SURVEY,
    name: 'Customer Feedback & Surveys',
    description: 'Post-call CSAT, NPS surveys, rating collection, and satisfaction analytics.',
    category: 'analytics',
    isCore: false
  }
};

export const getFeatureLabel = (token: string): string => {
  return FEATURE_METADATA[token as FeatureToken]?.name || token;
};

export const getFeatureDescription = (token: string): string => {
  return (
    FEATURE_METADATA[token as FeatureToken]?.description ||
    'This feature is not enabled for your organization. Contact your administrator to upgrade.'
  );
};
